import type { ComponentType } from "react";

/** Props passed to every fenced-directive component. */
export interface DirectiveProps {
  meta: Record<string, string>;
  body: string;
}

/** Parsed result of a fenced directive with YAML-like front matter. */
export interface FencedDirective {
  meta: Record<string, string>;
  body: string;
}

/** Contract for a custom fenced code block rendered by MarkdownWithHighlight. */
export interface MarkdownExtension {
  /** Language identifier after the opening fence (e.g. "pmc-figure"). */
  language: string;
  /** Component that renders the parsed directive. */
  Component: ComponentType<DirectiveProps>;
}

/**
 * Parses a fenced code block body with the format:
 *   ---
 *   key: "value"
 *   ---
 *   body content
 *
 * Values must be double-quoted strings — the same simple format tangrowth's
 * _fig_to_directive and _table_wrap_to_directive write. A full YAML parser is
 * unnecessary: the format is written by a single owner and is always simple
 * double-quoted string values. Returns null when the opening --- delimiters are absent.
 */
export function parseFencedDirective(raw: string): FencedDirective | null {
  const SEP = "\n---\n";
  const openEnd = raw.startsWith("---\n") ? raw.indexOf(SEP, 4) : -1;
  if (openEnd === -1) return null;

  const meta: Record<string, string> = {};
  for (const line of raw.slice(4, openEnd).split("\n")) {
    const colon = line.indexOf(": ");
    if (colon !== -1) {
      meta[line.slice(0, colon)] = line.slice(colon + 2).replace(/^"(.*)"$/, "$1");
    }
  }
  return { meta, body: raw.slice(openEnd + SEP.length).trim() };
}
